
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SilentAuthService {
  private readonly IFRAME_ID = 'silent-auth-iframe';
  private readonly SILENT_REFRESH_TIMEOUT = 60000; // 1 minuto timeout
  private readonly SILENT_REDIRECT_URI = `${window.location.origin}/silent-refresh.html`;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthService
  ) { }

  private createHiddenIframe(): HTMLIFrameElement {
    const iframe = this.document.createElement('iframe');
    iframe.id = this.IFRAME_ID;
    iframe.style.display = 'none';
    this.document.body.appendChild(iframe);
    return iframe;
  }

  private removeIframe(): void {
    const iframe = this.document.getElementById(this.IFRAME_ID);
    if (iframe) {
      iframe.remove();
    }
  }

  async performSilentAuth(): Promise<string> {
    const iframe = this.createHiddenIframe();
    
    try {
      // Gerar novo code verifier e challenge para o silent auth
      const codeVerifier = await this.authService.generateNewCodeVerifier();
      const codeChallenge = await this.authService.generateCodeChallenge(codeVerifier);

      // Construir a URL de autorização com prompt=none
      const authUrl = `${environment.authServer}/oauth2/authorize?` +
        `response_type=code` +
        `&client_id=public-client` +
        `&redirect_uri=${encodeURIComponent(this.SILENT_REDIRECT_URI)}` +
        `&scope=openid%20profile%20offline_access` +
        `&code_challenge=${codeChallenge}` +
        `&code_challenge_method=S256` +
        `&prompt=none`;


      const silentAuthPromise = new Promise<string>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          console.error('Silent auth timeout reached');
          reject(new Error('Silent authentication timeout'));
        }, this.SILENT_REFRESH_TIMEOUT);

        // Listener para mensagens do iframe
        const messageHandler = (event: MessageEvent) => {
          if (event.origin.split(':')[1] === environment.authServer.split(':')[1]) {
            const code = event.data.code;
            if (code) {
              clearTimeout(timeoutId);
              window.removeEventListener('message', messageHandler);
              resolve(code);
            } else if (event.data.error) {
              console.error('Auth server returned error:', event.data.error);
              clearTimeout(timeoutId);
              window.removeEventListener('message', messageHandler);
              reject(new Error(event.data.error));
            }
          }
        };

        window.addEventListener('message', messageHandler);
      });

      // Carregar a URL no iframe
      iframe.src = authUrl;

      // Aguardar o código de autorização
      const code = await silentAuthPromise;
      
      // Trocar o código por um novo token
      return this.authService.handleSilentCallback(code, codeVerifier, this.SILENT_REDIRECT_URI);

    } catch (error) {
      console.error('Silent auth error:', error);
      throw error;
    } finally {
      this.removeIframe();
    }
  }

  setupAutoRenewal(): void {
    // Verificar o token a cada minuto
    setInterval(() => {
      const expireAt = localStorage.getItem('expireAt');
      if (!expireAt) return;

      const expireDate = new Date(expireAt);
      const now = new Date();
      const timeUntilExpire = expireDate.getTime() - now.getTime();

      // Se faltar menos de 5 minutos para expirar, tentar renovar
      if (timeUntilExpire < 300000) { // 5 minutos em milissegundos
        this.performSilentAuth().catch(error => {
          console.error('Failed to perform silent authentication:', error);
        });
      }
    }, 60000); // Verificar a cada minuto
  }
} 